import React, { useEffect, useRef, useState } from 'react';
import { Group, Text } from '@mantine/core';
import MicRecorder from 'mic-recorder-to-mp3';
import StopIcon from '@material-ui/icons/Stop';
import CloseIcon from '@material-ui/icons/Close';

const formatElapsed = (seconds) => {
  const mm = Math.floor(seconds / 60).toString().padStart(2, '0');
  const ss = (seconds % 60).toString().padStart(2, '0');
  return `${mm}:${ss}`;
};

// The non-live counterpart to InlineVoiceRecorder: same inline dot+timer+stop/cancel UI in
// the prompt bar (no popup), but records the whole clip locally and hands a single file to
// onFinish instead of streaming to /ws/transcribe — backs the old record-then-POST-/transcribe
// flow while LIVE_TRANSCRIPTION_ENABLED is off (see aiAgentApi.js). Replaces the old
// TranscribeModal popup, which is what the "inconvenient overlay" feedback was about in the
// first place — the popup itself was never a requirement of the old transcription mechanism,
// just how it happened to be wrapped.
export default function InlineAudioRecorder({ onFinish, onCancel }) {
  const [elapsed, setElapsed] = useState(0);
  const recorderRef = useRef(null);
  const settledRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    const recorder = new MicRecorder({ bitRate: 128 });
    recorderRef.current = recorder;

    recorder.start().catch((err) => {
      console.error('Microphone access failed:', err);
      if (!cancelled && !settledRef.current) {
        settledRef.current = true;
        onCancel();
      }
    });

    const interval = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleStop = () => {
    if (settledRef.current) return;
    settledRef.current = true;
    recorderRef.current
      ?.stop()
      .getMp3()
      .then(([buffer, blob]) => {
        const file = new File(buffer, 'voice.mp3', { type: blob.type, lastModified: Date.now() });
        onFinish(file);
      })
      .catch((err) => {
        console.error('Recording could not be finalized:', err);
        onCancel();
      });
  };

  const handleCancel = () => {
    if (settledRef.current) return;
    settledRef.current = true;
    recorderRef.current?.stop();
    onCancel();
  };

  return (
    <Group wrap="nowrap" gap={6} align="center" justify="center" className="ai-inline-voice-recorder">
      <span className="ai-inline-voice-recorder-dot" />
      <Text className="ai-inline-voice-recorder-timer">{formatElapsed(elapsed)}</Text>
      <CloseIcon
        style={{ fontSize: '15px', color: '#9CA3AF', cursor: 'pointer' }}
        onClick={handleCancel}
        aria-label="Aufnahme abbrechen"
      />
      <StopIcon
        style={{ fontSize: '17px', color: '#5555FF', cursor: 'pointer' }}
        onClick={handleStop}
        aria-label="Aufnahme beenden"
      />
    </Group>
  );
}
